import { parseTermsBody, type TermsText } from '../lib/terms'
import { useHubLang, dateLocaleFor } from '../lib/i18n'
import { TERMS } from '../lib/hubCopy'

/**
 * 165. One terms document, as terms_text() returned it: title, version line,
 * and the body parsed into headings, paragraphs and lists (lib/terms.ts) —
 * never rendered as HTML.
 *
 * A Spanish reader sees the Spanish while it still matches its English (the
 * server decides), marked as a machine translation until a native reader
 * approves it, with a switch to the English. `onSwitchLocale` is optional: the
 * row renders only when the host can act on it.
 */
export function TermsDocument({
  doc,
  headingLevel = 2,
  onSwitchLocale,
}: {
  doc: TermsText
  headingLevel?: 1 | 2
  /** Ask for the other language: 'en-CA' from Spanish, null back to the reader's own. */
  onSwitchLocale?: (locale: string | null) => void
}) {
  const lang = useHubLang()
  const t = TERMS[lang]
  const date = new Date(doc.effectiveAt).toLocaleDateString(dateLocaleFor(lang), {
    year: 'numeric', month: 'long', day: 'numeric',
  })
  const Title = headingLevel === 1 ? 'h1' : 'h2'
  const Section = headingLevel === 1 ? 'h2' : 'h3'
  const showingEnglishToSpanish = lang === 'es' && doc.locale === 'en-CA'

  return (
    <article className="termsdoc" lang={doc.locale === 'es-CL' ? 'es' : 'en'}>
      <Title className="termsdoc__title">{doc.title}</Title>
      <p className="termsdoc__meta">{t.versionLine(doc.version, date)}</p>
      {lang === 'es' && onSwitchLocale && (
        <p className="termsdoc__lang">
          {doc.translationStatus === 'machine' && doc.locale === 'es-CL' && (
            <span className="termsdoc__machine">{t.machineNote} </span>
          )}
          <button
            type="button"
            className="btn btn--link"
            onClick={() => onSwitchLocale(showingEnglishToSpanish ? null : 'en-CA')}
          >
            {showingEnglishToSpanish ? t.readOwn : t.readEnglish}
          </button>
        </p>
      )}
      {parseTermsBody(doc.body).map((b, i) =>
        b.kind === 'heading' ? (
          <Section key={i} className="termsdoc__h">{b.text}</Section>
        ) : b.kind === 'list' ? (
          <ul key={i} className="termsdoc__list">
            {b.items.map((it, j) => <li key={j}>{it}</li>)}
          </ul>
        ) : (
          <p key={i} className="termsdoc__p">{b.text}</p>
        ),
      )}
    </article>
  )
}
